
import React from 'react';
import { ViewType, CompanyUser } from '@/types/lead';
import { Button } from '@/components/ui/button';
import { Grid, Rows, Table2, Star } from 'lucide-react';
import CategoryFilter from '@/components/CategoryFilter';
import CSVExportButton from '@/components/leads/csv/CSVExportButton';
import CSVImportButton from '@/components/leads/csv/CSVImportButton';
import UserLeadFilter from '@/components/UserLeadFilter';
import { cn } from '@/lib/utils';

interface LeadsHeaderProps {
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  totalLeads: number;
  showFavoritesOnly: boolean;
  toggleFavoritesFilter: () => void;
  categories: string[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  isLoading: boolean;
  companyUsers: CompanyUser[];
  selectedUserId: string | null;
  onUserSelect: (userId: string | null) => void;
  isLoadingUsers: boolean;
  filteredLeads: any[];
  onImportComplete?: () => void;
}

const LeadsHeader: React.FC<LeadsHeaderProps> = ({
  currentView,
  setCurrentView,
  totalLeads,
  showFavoritesOnly,
  toggleFavoritesFilter,
  categories,
  selectedCategory,
  setSelectedCategory,
  isLoading,
  companyUsers,
  selectedUserId,
  onUserSelect,
  isLoadingUsers,
  filteredLeads,
  onImportComplete
}) => {
  return (
    <div className="space-y-5 mb-6 animate-fade-in">
      {/* Header with modern styling */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 md:p-5 rounded-xl bg-gradient-to-br from-background via-muted/20 to-background border border-border/50 shadow-sm">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
            Leads
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            <span className="font-semibold text-foreground">{totalLeads}</span> {totalLeads === 1 ? 'lead' : 'leads'} found
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Modern View Switcher */}
          <div className="inline-flex items-center bg-muted/70 rounded-lg p-0.5 sm:p-1 gap-0.5 sm:gap-1 shadow-sm border border-border/60">
            <Button
              variant={currentView === 'card' ? "default" : "ghost"}
              size="sm"
              onClick={() => setCurrentView('card')}
              className={cn(
                "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                currentView === 'card' && "shadow-md elevation-lifted"
              )}
            >
              <Grid className="h-4 w-4 mr-2" />
              <span className="hidden md:inline text-xs font-medium">Cards</span>
            </Button>
            <Button
              variant={currentView === 'list' ? "default" : "ghost"}
              size="sm"
              onClick={() => setCurrentView('list')}
              className={cn(
                "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                currentView === 'list' && "shadow-md elevation-lifted"
              )}
            >
              <Rows className="h-4 w-4 mr-2" />
              <span className="hidden md:inline text-xs font-medium">List</span>
            </Button>
            <Button
              variant={currentView === 'table' ? "default" : "ghost"}
              size="sm"
              onClick={() => setCurrentView('table')}
              className={cn(
                "h-8 sm:h-9 px-2 sm:px-3 md:px-4 transition-all text-xs sm:text-sm",
                currentView === 'table' && "shadow-md elevation-lifted"
              )}
            >
              <Table2 className="h-4 w-4 mr-2" />
              <span className="hidden md:inline text-xs font-medium">Table</span>
            </Button>
          </div>
          
          <Button
            variant={showFavoritesOnly ? "default" : "outline"}
            size="sm"
            onClick={toggleFavoritesFilter}
            className={cn(
              "whitespace-nowrap hover:scale-105 transition-all gap-1 sm:gap-2 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm",
              showFavoritesOnly && "shadow-md"
            )}
          >
            <Star className={cn("h-4 w-4", showFavoritesOnly && "fill-current")} />
            {showFavoritesOnly ? "All Leads" : "Favorites"}
          </Button>
          
          {/* CSV buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <CSVImportButton onImportComplete={onImportComplete} />
            <CSVExportButton leads={filteredLeads} />
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div id="mobile-filters-section" className="flex flex-col sm:flex-row gap-3 p-3 sm:p-4 rounded-lg bg-muted/40 border border-border/60 shadow-sm scroll-mt-24">
        <div className="flex-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
            Filter by Category
          </label>
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            isLoading={isLoading}
          />
        </div>
        
        <div className="sm:w-auto sm:border-l sm:border-border/60 sm:pl-3">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
            Filter by User
          </label>
          <UserLeadFilter
            companyUsers={companyUsers}
            selectedUserId={selectedUserId}
            onUserSelect={onUserSelect}
            isLoading={isLoadingUsers}
          />
        </div>
      </div>
    </div>
  );
};

export default LeadsHeader;
